import { Country } from '@commenda/prisma';
import { JurisdictionInterface } from '@commenda/types/src/corporation';
import { useMemo } from 'react';
// Import the module, not the package barrel: @commenda/api re-exports this file, so the barrel
// would make the two packages import each other.
import { useSupportedJurisdictions } from '@commenda/api/business-entity/useSupportedEntities';

/**
 * What the content knows about the jurisdictions the product supports.
 *
 * The backend sends every jurisdiction it offers an entity type in, each with its id, what to call
 * it and its subdivision code. This is the only place the app should learn any of that: an id is
 * opaque, so a label or a subdivision code has to be looked up rather than derived, and the
 * content is the one that decides both.
 */
export function useJurisdictions({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const { data } = useSupportedJurisdictions({ enabled });

  const byId = useMemo(() => {
    const jurisdictions = new Map<string, JurisdictionInterface>();
    for (const forCountry of Object.values(data?.jurisdictions ?? {})) {
      for (const jurisdiction of Object.values(forCountry ?? {})) {
        if (jurisdiction?.id) jurisdictions.set(jurisdiction.id, jurisdiction);
      }
    }
    return jurisdictions;
  }, [data]);

  // Keyed by country: TN is both Tennessee and Tamil Nadu.
  const byCountryAndCode = useMemo(
    () =>
      new Map(
        Object.entries(data?.jurisdictions ?? {}).flatMap(
          ([country, forCountry]) =>
            Object.values(forCountry ?? {})
              .filter(
                (jurisdiction): jurisdiction is JurisdictionInterface =>
                  Boolean(jurisdiction?.id && jurisdiction.subNationalCode)
              )
              .map(
                (jurisdiction) =>
                  [
                    `${country}:${jurisdiction.subNationalCode}`,
                    jurisdiction.id,
                  ] as const
              )
        )
      ),
    [data]
  );

  return useMemo(
    () => ({
      /** Whether the content has been read yet. A label is the id until it has. */
      isLoaded: byId.size > 0,

      /** What to call a jurisdiction. Falls back to the id, which is at least stable and specific. */
      labelOf: (jurisdictionId?: string | null): string =>
        jurisdictionId
          ? (byId.get(jurisdictionId)?.displayName ?? jurisdictionId)
          : '',

      /** The subdivision code an address or an identifier is keyed by. Null at country level. */
      subNationalCodeOf: (jurisdictionId?: string | null): string | null =>
        jurisdictionId
          ? (byId.get(jurisdictionId)?.subNationalCode ?? null)
          : null,

      idOf: (
        jurisdiction?: string | null,
        country?: Country | null
      ): string | null => {
        if (!jurisdiction) return null;
        if (byId.has(jurisdiction)) return jurisdiction;
        return country
          ? (byCountryAndCode.get(`${country}:${jurisdiction}`) ?? null)
          : null;
      },

      /** Every jurisdiction a country offers an entity type in, for a picker. */
      optionsFor: (
        country?: Country | null
      ): { value: string; label: string }[] =>
        country
          ? Object.values(data?.jurisdictions?.[country] ?? {})
              .filter(
                (jurisdiction): jurisdiction is JurisdictionInterface =>
                  Boolean(jurisdiction?.id)
              )
              .map((jurisdiction) => ({
                value: jurisdiction.id,
                label: jurisdiction.displayName,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
          : [],
    }),
    [byId, byCountryAndCode, data]
  );
}
